// ClassificationPage.js
// Estándares de clasificación IPL + calculadora de DOTS / Wilks / GL Points.
import React, { useEffect, useMemo, useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import PageHero from './PageHero';
import { getAllCMSImages } from '../services/s3Service';
import { getFirstCachedImage } from '../utils/cmsCache';
import { dots, wilks, glPoints, weightClassFor, KG_PER_LB, LB_PER_KG } from '../utils/powerliftingFormulas';
import '../styles/classification/ClassificationPage.css';

const GENDER_LABELS = { Male: 'Masculino', Female: 'Femenino' };

const AGE_LABELS = {
  OPEN: 'Open',
  'Junior 15-19': 'Junior 15-19',
  'Junior 20-23': 'Junior 20-23',
  '40-44': 'Máster 40-44',
  '45-49': 'Máster 45-49',
  '50-54': 'Máster 50-54',
  '55-59': 'Máster 55-59',
  '60-64': 'Máster 60-64',
  '65-69': 'Máster 65-69',
  '70-74': 'Máster 70-74',
  '75-79': 'Máster 75-79',
  '80+': 'Máster 80+',
};

const EVENT_LABELS = {
  'Full Power': 'Powerlifting (total)',
  SQ: 'Solo sentadilla',
  BP: 'Solo press banca',
  DL: 'Solo peso muerto',
};

const GEAR_LABELS = {
  Raw: 'Raw (sin vendas)',
  'Cl Raw': 'Classic Raw (con vendas)',
  SP: 'Equipado single-ply',
  MP: 'Equipado multi-ply',
};

const CLASS_TONE = {
  "Int'l Elite": 'intl',
  Elite: 'elite',
  Master: 'master',
};

const LIFT_FIELDS = {
  'Full Power': [
    { key: 'sq', label: 'Sentadilla' },
    { key: 'bp', label: 'Press banca' },
    { key: 'dl', label: 'Peso muerto' },
  ],
  SQ: [{ key: 'sq', label: 'Sentadilla' }],
  BP: [{ key: 'bp', label: 'Press banca' }],
  DL: [{ key: 'dl', label: 'Peso muerto' }],
};

const num = (v) => {
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};

const fmt = (n, decimals = 1) =>
  Number.isFinite(n)
    ? n.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: decimals })
    : '—';

const fmtScore = (n) =>
  Number.isFinite(n) ? n.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

// Igual que el calculador oficial: redondea hacia arriba a 0,1 lb.
const kgToDisplay = (kg, unit) => (unit === 'lb' ? Math.ceil(kg * LB_PER_KG * 10) / 10 : kg);

const ClassificationPage = () => {
  const [heroImage, setHeroImage] = useState(() => getFirstCachedImage(['page_hero_clasificacion', 'page_hero_records']));
  const [ipl, setIpl] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const [gender, setGender] = useState('Male');
  const [age, setAge] = useState('OPEN');
  const [event, setEvent] = useState('Full Power');
  const [gear, setGear] = useState('Raw');
  const [unit, setUnit] = useState('kg');
  const [bodyweight, setBodyweight] = useState('');
  const [manualClass, setManualClass] = useState('auto');
  const [lifts, setLifts] = useState({ sq: '', bp: '', dl: '' });
  const [showFullTable, setShowFullTable] = useState(false);

  useEffect(() => {
    getAllCMSImages()
      .then((imgs) => setHeroImage(imgs?.page_hero_clasificacion || imgs?.page_hero_records || null))
      .catch(() => setHeroImage(null));
    // Los datos (~100 KB) se cargan en un chunk aparte para no inflar el bundle principal.
    import('../data/iplClassification')
      .then((mod) => setIpl(mod))
      .catch(() => setLoadError(true));
  }, []);

  // Al cambiar de sexo, la categoría manual puede dejar de existir.
  useEffect(() => {
    setManualClass('auto');
  }, [gender]);

  const toKg = (v) => (unit === 'lb' ? v * KG_PER_LB : v);

  const bwKg = toKg(num(bodyweight));
  const weightClasses = useMemo(() => ipl?.WEIGHT_CLASSES[gender] || [], [ipl, gender]);
  const autoClass = weightClassFor(bwKg, weightClasses);
  const weightClass = manualClass !== 'auto' ? manualClass : autoClass;

  const fields = LIFT_FIELDS[event];
  const liftKg = useMemo(() => {
    const values = fields.map((f) => num(lifts[f.key]));
    if (values.some((v) => !Number.isFinite(v) || v <= 0)) return NaN;
    return values.reduce((a, b) => a + b, 0) * (unit === 'lb' ? KG_PER_LB : 1);
  }, [fields, lifts, unit]);

  const standards = useMemo(
    () => (ipl && weightClass ? ipl.getStandards(gender, age, event, gear, weightClass) : null),
    [ipl, gender, age, event, gear, weightClass]
  );

  // Clase alcanzada = la más alta cuyo estándar se iguala o supera.
  const achievedIndex = useMemo(() => {
    if (!standards || !Number.isFinite(liftKg)) return -1;
    return standards.findIndex((s) => liftKg >= s.kg - 1e-9);
  }, [standards, liftKg]);

  const achieved = achievedIndex >= 0 ? standards[achievedIndex] : null;
  const nextTarget =
    standards && Number.isFinite(liftKg)
      ? achievedIndex === -1
        ? standards[standards.length - 1]
        : achievedIndex > 0
        ? standards[achievedIndex - 1]
        : null
      : null;

  const equipped = gear === 'SP' || gear === 'MP';
  const scores = {
    dots: dots(gender, bwKg, liftKg),
    wilks: wilks(gender, bwKg, liftKg),
    gl:
      event === 'Full Power'
        ? glPoints(gender, bwKg, liftKg, 'total', equipped)
        : event === 'BP'
        ? glPoints(gender, bwKg, liftKg, 'bench', equipped)
        : null,
  };

  const fullTable = useMemo(() => {
    if (!ipl || !showFullTable) return [];
    return weightClasses.map((wc) => ({ wc, rows: ipl.getStandards(gender, age, event, gear, wc) }));
  }, [ipl, showFullTable, weightClasses, gender, age, event, gear]);

  const hasInput = Number.isFinite(bwKg) && bwKg > 0 && Number.isFinite(liftKg);

  const resetAll = () => {
    setBodyweight('');
    setLifts({ sq: '', bp: '', dl: '' });
    setManualClass('auto');
  };

  return (
    <div className="clasif-page">
      <Header />
      <PageHero
        eyebrow="Resultados"
        imageUrl={heroImage}
        title="Clasificación y DOTS"
        subtitle="Revisa tu nivel según los estándares oficiales de la IPL y calcula tus puntos DOTS, Wilks y GL Points"
      />

      <main className="clasif-wrap">
        {loadError && (
          <div className="clasif-notice">No se pudieron cargar los estándares de clasificación. Intenta recargar la página.</div>
        )}

        <div className="clasif-grid">
          {/* ── Formulario ─────────────────────────────────────────── */}
          <section className="clasif-card clasif-form" aria-label="Datos del atleta">
            <div className="clasif-card-head">
              <h2>Tus datos</h2>
              <div className="clasif-units" role="group" aria-label="Unidades">
                {['kg', 'lb'].map((u) => (
                  <button
                    key={u}
                    type="button"
                    className={unit === u ? 'is-active' : ''}
                    onClick={() => setUnit(u)}
                    aria-pressed={unit === u}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>

            <div className="clasif-fields">
              <label className="clasif-field">
                <span>Sexo</span>
                <select value={gender} onChange={(e) => setGender(e.target.value)}>
                  {Object.entries(GENDER_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>

              <label className="clasif-field">
                <span>Categoría de edad</span>
                <select value={age} onChange={(e) => setAge(e.target.value)}>
                  {Object.entries(AGE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>

              <label className="clasif-field">
                <span>Evento</span>
                <select value={event} onChange={(e) => setEvent(e.target.value)}>
                  {Object.entries(EVENT_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>

              <label className="clasif-field">
                <span>Equipamiento</span>
                <select value={gear} onChange={(e) => setGear(e.target.value)}>
                  {Object.entries(GEAR_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>

              <label className="clasif-field">
                <span>Peso corporal ({unit})</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.1"
                  placeholder={unit === 'kg' ? 'Ej: 82.3' : 'Ej: 181.4'}
                  value={bodyweight}
                  onChange={(e) => setBodyweight(e.target.value)}
                />
              </label>

              <label className="clasif-field">
                <span>Categoría de peso</span>
                <select value={manualClass} onChange={(e) => setManualClass(e.target.value)}>
                  <option value="auto">
                    {autoClass ? `Automática (${autoClass} kg)` : 'Automática según peso'}
                  </option>
                  {weightClasses.map((wc) => (
                    <option key={wc} value={wc}>{wc} kg</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="clasif-divider" />

            <div className={`clasif-fields clasif-lifts clasif-lifts--${fields.length}`}>
              {fields.map((f) => (
                <label key={f.key} className="clasif-field">
                  <span>{f.label} ({unit})</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.5"
                    placeholder="0"
                    value={lifts[f.key]}
                    onChange={(e) => setLifts((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  />
                </label>
              ))}
            </div>

            {event === 'Full Power' && (
              <div className="clasif-total">
                <span>Total</span>
                <strong>
                  {Number.isFinite(liftKg) ? `${fmt(unit === 'lb' ? liftKg * LB_PER_KG : liftKg)} ${unit}` : '—'}
                </strong>
              </div>
            )}

            <button type="button" className="clasif-reset" onClick={resetAll}>
              Limpiar
            </button>
          </section>

          {/* ── Resultados ─────────────────────────────────────────── */}
          <section className="clasif-results" aria-live="polite" aria-label="Resultados">
            <div className={`clasif-card clasif-verdict ${achieved ? `tone-${CLASS_TONE[achieved.name] || 'class'}` : ''}`}>
              <span className="clasif-eyebrow">Tu clasificación IPL</span>
              {!hasInput ? (
                <p className="clasif-verdict-empty">Ingresa tu peso corporal y tus levantamientos para ver tu nivel.</p>
              ) : !standards ? (
                <p className="clasif-verdict-empty">Cargando estándares…</p>
              ) : (
                <>
                  <h3 className="clasif-verdict-title">{achieved ? achieved.name : 'Bajo Class IV'}</h3>
                  <p className="clasif-verdict-meta">
                    {GENDER_LABELS[gender]} · {AGE_LABELS[age]} · {weightClass} kg · {GEAR_LABELS[gear]}
                  </p>
                  {nextTarget && (
                    <p className="clasif-verdict-next">
                      Te faltan{' '}
                      <strong>
                        {fmt(kgToDisplay(nextTarget.kg, unit) - (unit === 'lb' ? liftKg * LB_PER_KG : liftKg))} {unit}
                      </strong>{' '}
                      para <strong>{nextTarget.name}</strong>
                    </p>
                  )}
                  {achievedIndex === 0 && <p className="clasif-verdict-next">Nivel máximo alcanzado. 🏆</p>}
                </>
              )}
            </div>

            <div className="clasif-scores">
              <div className="clasif-card clasif-score clasif-score--main">
                <span className="clasif-eyebrow">DOTS</span>
                <strong>{fmtScore(scores.dots)}</strong>
              </div>
              <div className="clasif-card clasif-score">
                <span className="clasif-eyebrow">Wilks</span>
                <strong>{fmtScore(scores.wilks)}</strong>
              </div>
              <div className="clasif-card clasif-score">
                <span className="clasif-eyebrow">GL Points</span>
                <strong>{fmtScore(scores.gl)}</strong>
                {(event === 'SQ' || event === 'DL') && <small>Solo aplica a total o banca</small>}
              </div>
            </div>

            <div className="clasif-card">
              <div className="clasif-card-head">
                <h2>Estándares {weightClass ? `· ${weightClass} kg` : ''}</h2>
                <span className="clasif-muted">{EVENT_LABELS[event]}</span>
              </div>
              {!weightClass ? (
                <p className="clasif-muted">Ingresa tu peso corporal o elige una categoría de peso.</p>
              ) : !standards ? (
                <p className="clasif-muted">Cargando…</p>
              ) : (
                <ul className="clasif-ladder">
                  {standards.map((s, i) => {
                    const reached = achievedIndex !== -1 && i >= achievedIndex;
                    const pct = Number.isFinite(liftKg) ? Math.min(100, (liftKg / s.kg) * 100) : 0;
                    return (
                      <li
                        key={s.name}
                        className={`${reached ? 'is-reached' : ''} ${i === achievedIndex ? 'is-current' : ''}`}
                      >
                        <span className="clasif-ladder-name">{s.name}</span>
                        <span className="clasif-ladder-bar" aria-hidden="true">
                          <span style={{ width: `${pct}%` }} />
                        </span>
                        <span className="clasif-ladder-kg">
                          {fmt(kgToDisplay(s.kg, unit))} {unit}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        </div>

        {/* ── Tabla completa ─────────────────────────────────────────── */}
        <section className="clasif-card clasif-full">
          <div className="clasif-card-head">
            <h2>Tabla completa</h2>
            <button type="button" className="clasif-toggle" onClick={() => setShowFullTable((v) => !v)}>
              {showFullTable ? 'Ocultar' : 'Ver todas las categorías'}
            </button>
          </div>
          <p className="clasif-muted">
            {GENDER_LABELS[gender]} · {AGE_LABELS[age]} · {EVENT_LABELS[event]} · {GEAR_LABELS[gear]} · valores en {unit}
          </p>
          {showFullTable && ipl && (
            <div className="clasif-table-wrap">
              <table className="clasif-table">
                <thead>
                  <tr>
                    <th>Categoría</th>
                    {ipl.CLASS_NAMES.map((c) => (
                      <th key={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fullTable.map(({ wc, rows }) => (
                    <tr key={wc} className={wc === weightClass ? 'is-current' : ''}>
                      <th scope="row">{wc} kg</th>
                      {rows.map((r) => (
                        <td key={r.name}>{fmt(kgToDisplay(r.kg, unit))}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="clasif-info">
          <div>
            <h3>¿Qué es DOTS?</h3>
            <p>
              DOTS (Dynamic Objective Team Scoring) es la fórmula que usa la IPL para comparar levantadores de distinto
              peso corporal y definir al Mejor Levantador. Mientras más alto el puntaje, más fuerte eres en relación a tu
              peso.
            </p>
          </div>
          <div>
            <h3>Sobre los estándares</h3>
            <p>
              Los totales mínimos de cada nivel (Class IV a Int&apos;l Elite) corresponden a los{' '}
              <a
                href="https://internationalpowerliftingleague.com/classification-standards/"
                target="_blank"
                rel="noopener noreferrer"
              >
                estándares de clasificación oficiales de la IPL
              </a>
              . Raw permite solo rodilleras de neopreno; Classic Raw permite además vendas de rodilla.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ClassificationPage;
